import { format, parseISO } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { jwtDecode } from "jwt-decode";
export const truncateString = (str: string): string => {
  if (str.length <= 10) {
    return str; // No need to truncate if the string is too short
  }
  const firstThree = str.slice(0, 3);
  const lastThree = str.slice(-3);
  return `${firstThree}...${lastThree}`;
};

export const convertBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return "0 Bytes";
  const kb = 1024;
  const correctDecimals = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(kb));

  return parseFloat((bytes / Math.pow(kb, i)).toFixed(correctDecimals)) + " " + sizes[i];
};

export const getInitials = (email: string) => {
  const namePart = email.split("@")[0];
  const nameParts = namePart.split(/[\._]/);
  if (nameParts.length === 1) {
    const firstLetter = nameParts[0][0].toUpperCase();
    const lastLetter = nameParts[0][nameParts[0].length - 1].toUpperCase();
    return `${firstLetter}${lastLetter}`;
  }
  const firstLetter = nameParts[0][0].toUpperCase();
  const lastLetter = nameParts[nameParts.length - 1][nameParts[nameParts.length - 1].length - 1].toUpperCase();
  return `${firstLetter}${lastLetter}`;
};

export const JWTDecoder = (token: string) => {
  const decoded = jwtDecode(token);
  const expTime = decoded.exp;
  return expTime;
};

export const convertToLocalString = (isoString: string) => {
  const date = parseISO(isoString);
  // let newDate = format(date, "EEE MMM dd yyyy HH:mm:ss 'GMT'XXX (zzzz)", { locale: enUS });
  let newDate = format(date, "M/d/yyyy", { locale: enUS });
  return newDate;
};

export const convertToISO = (dateString: string) => {
  const date = new Date(dateString);
  return date.toISOString();
};

export const extractFormFields = (data: any): any[] => {
  if (!data) return [];

  // If the data is an array, recursively process each item
  if (Array.isArray(data)) {
    return data.flatMap((item) => extractFormFields(item));
  }

  // If the data is an object, check for fields and recursively process nested objects
  if (typeof data === "object") {
    const fields: any[] = [];

    // Check if the current object contains form fields (e.g., has a `type` or `label` property)
    if (data.type || data.label) {
      fields.push(data);
    }

    // Recursively process nested objects
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        fields.push(...extractFormFields(data[key]));
      }
    }

    return fields;
  }

  // If the data is not an object or array, return an empty array
  return [];
};

export const getFormFieldsByKey = (formDataForServer: any) => {
  if (!formDataForServer) return [];

  const fields: { key: string; fields: any[] }[] = [];

  for (const key in formDataForServer) {
    if (formDataForServer.hasOwnProperty(key)) {
      const nestedData = formDataForServer[key];

      // Handle travelerInfo
      if (key === "travelerInfo" && nestedData?.traveler) {
        fields.push({ key, fields: nestedData.traveler });
      }

      // Handle otherInfoIndex
      else if (key.includes("otherInfo") && nestedData?.other) {
        fields.push({ key, fields: nestedData.other });
      }

      // Handle sponsorshipInfo
      else if (key === "sponsorshipInfo") {
        if (nestedData?.["sponsorship/general"]) {
          fields.push({ key: `sponsorship/general`, fields: nestedData["sponsorship/general"] });
        }
        if (nestedData?.["sponsorship/fromApplicants"]) {
          fields.push({ key: `sponsorship/fromApplicants`, fields: nestedData["sponsorship/fromApplicant"] });
        }
        if (nestedData?.["sponsorship/mySelf"]) {
          fields.push({ key: `sponsorship/mySelf`, fields: nestedData["sponsorship/mySelf"] });
        }
        if (nestedData?.["sponsorship/someoneElse"]) {
          fields.push({ key: `sponsorship/someoneElse`, fields: nestedData["sponsorship/someoneElse"] });
        }
      }

      // Handle other keys dynamically
      else if (Array.isArray(nestedData)) {
        fields.push({ key, fields: nestedData });
      } else if (typeof nestedData === "object") {
        fields.push({ key, fields: Object.values(nestedData) });
      }
    }
  }

  return fields;
};

const TAILWIND_MAIN_COLORS = [
  // Slate
  "#d4d4d8",
  "#a8a29e",
  "#64748b",

  // Orange
  "#f97316",
  "#f97316",
  // Amber
  "#f59e0b",
  // Yellow
  "#eab308",
  "#fcd34d",
  // Lime
  "#84cc16",
  // Green
  "#22c55e",
  // Emerald
  "#10b981",
  // Teal
  "#14b8a6",
  // Cyan
  "#06b6d4",
  // Sky
  "#0ea5e9",
  // Blue
  "#3b82f6",
  // Indigo
  "#6366f1",
  // Violet
  "#8b5cf6",
  // Purple
  "#a855f7",
  // Fuchsia
  "#d946ef",
  // Pink
  "#ec4899",
  // Red
  "#ef4444",
  // Rose
  "#f43f5e",
  "#f43f5e",
  // White and Black
  "#ffffff",
  "#000000",
];

// react quill full toolbar
export const FULL_TOOLBAR_OPTIONS = [
  [{ "header": [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"], // toggled buttons
  [{ "color": TAILWIND_MAIN_COLORS }, { "background": TAILWIND_MAIN_COLORS }], // dropdown with defaults from theme
  ["blockquote", "code-block"],
  [{ "list": "ordered" }, { "list": "bullet" }],
  [{ "script": "sub" }, { "script": "super" }], // superscript/subscript
  [{ "indent": "-1" }, { "indent": "+1" }], // outdent/indent
  [{ "direction": "rtl" }], // text direction
  [{ "font": [] }],
  [{ "align": [] }],
  ["clean"], // remove formatting button
  ["link", "image", "video"], // link and media
];
